import { create } from 'zustand';

import { ApiError, api } from '../lib/api';

/**
 * Favourites, as an overlay on whatever the server last said.
 *
 * ## Why a store and not per-component state
 *
 * The same track can be on screen three times at once: a row in a list, the
 * track page behind it, and the dock at the bottom playing it. Favouriting it
 * anywhere has to update all three, and the dock is the hard case - it outlives
 * navigation, so it cannot re-read a list that has since unmounted.
 *
 * So this holds only what has *changed* since the page loaded. Components read
 * `resolve(id, track.favorited)`: the overlay if this session has touched that
 * track, otherwise whatever came down with the data. That is deliberately not a
 * cache of every favourite the user has - a heavy user has thousands, and
 * fetching them to render one heart is the wrong trade.
 *
 * ## Optimistic, and reverted on failure
 *
 * A heart that waits for a round trip feels broken. So the overlay flips first
 * and the request follows; if it fails the overlay goes back to what it was,
 * which is the only honest end state - the server did not do it.
 */
interface FavoritesState {
  /** Only tracks touched this session. Absent means "ask the data". */
  overlay: Record<number, boolean>;
  /** Counts, likewise: only where this session has changed them. */
  counts: Record<number, number>;
  /** In-flight ids, so a double click cannot fire two requests. */
  pending: Record<number, true>;
  error: string | null;

  resolve: (trackId: number, fallback: boolean | undefined) => boolean;
  resolveCount: (trackId: number, fallback: number) => number;
  toggle: (trackId: number, currentlyFavorited: boolean, currentCount: number) => Promise<void>;
  /** Seed from a server response without making a request. */
  prime: (trackId: number, favorited: boolean, count?: number) => void;
  clear: () => void;
}

export const useFavorites = create<FavoritesState>((set, get) => ({
  overlay: {},
  counts: {},
  pending: {},
  error: null,

  resolve: (trackId, fallback) => get().overlay[trackId] ?? fallback ?? false,

  resolveCount: (trackId, fallback) => get().counts[trackId] ?? fallback,

  prime: (trackId, favorited, count) =>
    set((state) => ({
      overlay: { ...state.overlay, [trackId]: favorited },
      counts: count === undefined ? state.counts : { ...state.counts, [trackId]: count },
    })),

  toggle: async (trackId, currentlyFavorited, currentCount) => {
    // A second click while the first is in flight is a double click, not a
    // second intention. Ignoring it is what keeps the two from racing.
    if (get().pending[trackId]) return;

    const next = !currentlyFavorited;
    const optimisticCount = Math.max(0, currentCount + (next ? 1 : -1));

    set((state) => ({
      overlay: { ...state.overlay, [trackId]: next },
      counts: { ...state.counts, [trackId]: optimisticCount },
      pending: { ...state.pending, [trackId]: true },
      error: null,
    }));

    try {
      const result = await (next
        ? api.post<{ favorited: boolean; like_count: number }>(`/tracks/${trackId}/like`)
        : api.delete<{ favorited: boolean; like_count: number }>(`/tracks/${trackId}/like`));

      // Reconciled against what the server actually holds, which matters when
      // another tab changed it: the optimistic count was a guess from a number
      // that may already have been stale.
      set((state) => ({
        overlay: { ...state.overlay, [trackId]: result.favorited },
        counts: { ...state.counts, [trackId]: result.like_count },
      }));
    } catch (error) {
      set((state) => ({
        overlay: { ...state.overlay, [trackId]: currentlyFavorited },
        counts: { ...state.counts, [trackId]: currentCount },
        /*
         * 401 is not shown as an error.
         *
         * It means "you are signed out", which the button itself should be
         * handling by prompting a sign-in rather than by surfacing a failure.
         * Anything else is a real problem and worth saying out loud.
         */
        error:
          error instanceof ApiError && error.status === 401
            ? null
            : 'That did not save. Try again in a moment.',
      }));
    } finally {
      set((state) => {
        const pending = { ...state.pending };
        delete pending[trackId];

        return { pending };
      });
    }
  },

  // Called on sign-out. Without it the next person to sign in on this browser
  // inherits the last one's hearts until every list refetches.
  clear: () => set({ overlay: {}, counts: {}, pending: {}, error: null }),
}));
